import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useActualVsBudgetReportContext } from './context/ActualVsBudgetReportContext';
import { FIELD_ERROR, FIELD_HINT, FIELD_INPUT, FIELD_LABEL, FIELD_SELECT, ZONE, ZONE_GRID, ZONE_TITLE } from './helpers/configFieldStyles';
import {
    computePresetRange,
    monthRangeToDates,
    PERIOD_CURRENT_MONTH,
    PERIOD_CUSTOM,
    PERIOD_FULL_PROJECT,
    PERIOD_LAST_MONTH,
    PERIOD_LAST_QUARTER,
    PERIOD_YEAR_TO_DATE,
} from './helpers/reportPeriodPresets';
import ReportScopeSelector, { buildNodes, toApiPayload } from './ReportScopeSelector';
import SavedConfigBar from './SavedConfigBar';
import {
    COL_COMMITTED,
    COL_FORECAST,
    COL_MONTHLY_PHASING,
    COL_OPPORTUNITY,
    COL_PERCENT,
    COL_RISK,
    COL_UNCOMMITTED,
    PRESET_BOARD,
    PRESET_FULL,
    PRESET_STANDARD,
} from './types/ReportColumns';
import { FORMAT_BUDGET_CODE, FORMAT_GL_CODE } from './types/ReportFormat';
import { LEVEL_DEPARTMENT, LEVEL_DEPARTMENT_COST_CENTRE, LEVEL_FULL_DETAILS } from './types/ReportType';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import DatePickerField from 'CommonComponents/DatePickerField';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import ToggleFormField from 'CommonComponents/ToggleFormField';
import Tooltip from 'CommonComponents/Tooltip';
import { collectLeafIds } from 'CommonComponents/TreeMultiSelect';

import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import CircleInfoIcon from 'Assets/icons/circle-info.svg';

const ValidationSchema = Yup.object().shape({
    format: Yup.string().required($t('This value is required')),
    budget_id: Yup.string().required($t('This value is required')),
    start_date: Yup.date().required($t('This value is required')),
    end_date: Yup.date().required($t('This value is required')).min(Yup.ref('start_date'), $t('End month cannot be before start month')),
    exclude: Yup.string(),
    name: Yup.string().required($t('This value is required')),
});

const LEVEL_OPTIONS = [
    { value: LEVEL_DEPARTMENT, label: $t('Department') },
    { value: LEVEL_DEPARTMENT_COST_CENTRE, label: $t('Department + Cost Centre') },
    { value: LEVEL_FULL_DETAILS, label: $t('Full details') },
];

// What each level actually produces, so the choice is legible before generating.
const LEVEL_HINTS = {
    [LEVEL_DEPARTMENT]: $t('One row per department. Workforce Details is available at Full details only.'),
    [LEVEL_DEPARTMENT_COST_CENTRE]: $t('One row per cost centre, grouped under departments. Workforce Details is available at Full details only.'),
    [LEVEL_FULL_DETAILS]: $t('Every budget line, with project and budget/GL description columns.'),
};

const PERIOD_PRESET_OPTIONS = [
    { value: PERIOD_FULL_PROJECT, label: $t('Full project') },
    { value: PERIOD_YEAR_TO_DATE, label: $t('Year to date') },
    { value: PERIOD_CURRENT_MONTH, label: $t('Current month') },
    { value: PERIOD_LAST_MONTH, label: $t('Last month') },
    { value: PERIOD_LAST_QUARTER, label: $t('Last quarter') },
    { value: PERIOD_CUSTOM, label: $t('Custom') },
];

const COLUMN_PRESET_OPTIONS = [
    { value: PRESET_BOARD, label: $t('Board summary') },
    { value: PRESET_STANDARD, label: $t('Standard') },
    { value: PRESET_FULL, label: $t('Full') },
];

const CUSTOMISE_COLUMN_OPTIONS = [
    { key: COL_COMMITTED, label: $t('Committed') },
    { key: COL_UNCOMMITTED, label: $t('Uncommitted') },
    { key: COL_PERCENT, label: $t('%') },
    { key: COL_RISK, label: $t('Budget Risks') },
    { key: COL_OPPORTUNITY, label: $t('Budget Opportunities') },
    { key: COL_FORECAST, label: $t('Forecast') },
    { key: COL_MONTHLY_PHASING, label: $t('Monthly phasing') },
];

const FieldLabel = ({ children, htmlFor, required }) => (
    <label className={FIELD_LABEL} htmlFor={htmlFor}>
        {children} {required && <span className="tw-text-red">*</span>}
    </label>
);

// A labelled select on the configuration card. `name` doubles as the element id
// so the label's `for` lines up; `error` is the already-resolved message (falsy
// while the field is untouched).
const ConfigSelect = ({ name, label, required, value, disabled, error, onBlur, onChange, children }) => (
    <>
        <FieldLabel htmlFor={name} required={required}>
            {label}
        </FieldLabel>
        <select id={name} name={name} className={FIELD_SELECT} disabled={disabled} value={value} onBlur={onBlur} onChange={onChange}>
            {children}
        </select>
        {!!error && <div className={FIELD_ERROR}>{error}</div>}
    </>
);

export default function ActualVsBudgetReportCriteria(props) {
    const {
        configs,
        criteria,
        updateCriteria,
        level,
        updateLevel,
        periodPreset,
        setPeriodPreset,
        columnPreset,
        setColumnPreset,
        visibleColumns,
        setColumnVisibility,
        hideEmptyRows,
        setHideEmptyRows,
        reportConfigsIndexUrl,
        reportConfigsStoreUrl,
        reportConfigsDeleteUrl,
    } = useActualVsBudgetReportContext();
    const [departmentsList, setDepartmentsList] = useState({});
    const [selection, setSelection] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCustomiseColumns, setShowCustomiseColumns] = useState(false);

    const toast = useToast();

    // When restoring a saved configuration that also switches the budget
    // version, the scope leaf-ids must be applied AFTER the department set for
    // the new budget has been re-fetched and the tree rebuilt (otherwise the
    // "reset to all on department change" effect below would clobber them).
    const restoreScopeRef = useRef(null);

    const nodes = useMemo(() => buildNodes(departmentsList), [departmentsList]);

    const isCustomPeriod = periodPreset === PERIOD_CUSTOM;

    // Reset to "all selected" (the default, equivalent to "All Departments")
    // whenever the department set changes — e.g. after switching budget version.
    // Exception: a saved-configuration restore that changed the budget parks the
    // scope it wants in `restoreScopeRef`; apply that (dropping any leaf-ids that
    // no longer exist in the new tree) instead of resetting to all.
    useEffect(() => {
        if (restoreScopeRef.current !== null) {
            const leafSet = new Set(collectLeafIds(nodes));
            setSelection(restoreScopeRef.current.filter((id) => leafSet.has(id)));
            restoreScopeRef.current = null;
        } else {
            setSelection(collectLeafIds(nodes));
        }
    }, [nodes]);

    const fetchDepartments = (budgetId = null) => {
        let url = new URL(props.fetchDepartmentsUrl);
        let params = new URLSearchParams(url.search);
        if (budgetId) {
            params.set('budget_id', budgetId);
        }

        const fetchUrl = url.origin + url.pathname + '?' + params.toString();

        axios
            .get(fetchUrl)
            .then(({ data }) => {
                if (_.isObject(data.departments)) {
                    setDepartmentsList(data.departments);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                format: criteria.format || '',
                budget_id: criteria.budget_id || '',
                start_date: criteria.start_date || '',
                end_date: criteria.end_date || '',
                exclude: criteria.exclude || '',
                all_departments: criteria.all_departments,
                departments: criteria.departments,
                costcenters: criteria.costcenters || {},
                show_workforce_details: criteria.show_workforce_details || false,
                include_unallocated: criteria.include_unallocated !== false,
                comparison_budget_id: criteria.comparison_budget_id || '',
                name: '',
            }}
            validationSchema={ValidationSchema}
            onSubmit={(values, actions) => {
                setLoading(true);

                const { departments, costcenters } = toApiPayload(selection, nodes);

                updateCriteria({ ...values, departments, costcenters }).finally(() => {
                    actions.setSubmitting(false);
                    setLoading(false);
                });
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, handleBlur, setFieldValue }) => {
                useEffect(() => {
                    if (values.budget_id) {
                        fetchDepartments(values.budget_id);
                    }

                    // A version can't be compared with itself — drop the
                    // comparison if the user picks it as the primary version.
                    if (values.comparison_budget_id && String(values.comparison_budget_id) === String(values.budget_id)) {
                        setFieldValue('comparison_budget_id', '');
                    }
                }, [values.budget_id]);

                const controlsDisabled = isSubmitting || loading;

                // Apply a period preset: fills both month pickers (read-only)
                // from the pure preset math, or just unlocks them for Custom.
                const applyPeriodPreset = (preset) => {
                    setPeriodPreset(preset);

                    if (preset === PERIOD_CUSTOM) {
                        return;
                    }

                    const { startMonth, endMonth } = computePresetRange(preset, {
                        projectMin: configs.minDate,
                        projectMax: configs.maxDate,
                    });
                    const { start_date, end_date } = monthRangeToDates(startMonth, endMonth);

                    setFieldValue('start_date', start_date);
                    setFieldValue('end_date', end_date);
                };

                // Flat snapshot of EVERY control on the screen, for a saved
                // configuration (IB-834). Scope is stored as the raw leaf-id
                // array so a restore is exact.
                const buildSnapshot = () => ({
                    format: values.format,
                    budget_id: values.budget_id,
                    level,
                    period_preset: periodPreset,
                    start_date: values.start_date,
                    end_date: values.end_date,
                    scope: selection,
                    show_workforce_details: values.show_workforce_details,
                    include_unallocated: values.include_unallocated,
                    column_preset: columnPreset,
                    visible_columns: visibleColumns,
                    hide_empty_rows: hideEmptyRows,
                    comparison_budget_id: values.comparison_budget_id,
                });

                // Restore a saved configuration. "Load what matches, flag what
                // couldn't be applied": budget/comparison versions that no
                // longer exist are skipped (current value kept) and surfaced in
                // a toast; scope leaf-ids that no longer exist are dropped
                // silently; missing keys leave that control at its default.
                const applySnapshot = (parameters) => {
                    const params = parameters || {};
                    const skipped = [];
                    const budgetIds = new Set((configs.budgets || []).map(({ id }) => String(id)));

                    if (params.format !== undefined) {
                        setFieldValue('format', params.format);
                    }

                    if (params.level !== undefined) {
                        updateLevel(params.level);
                    }

                    if (params.period_preset !== undefined) {
                        setPeriodPreset(params.period_preset);
                    }
                    if (params.start_date !== undefined) {
                        setFieldValue('start_date', params.start_date);
                    }
                    if (params.end_date !== undefined) {
                        setFieldValue('end_date', params.end_date);
                    }

                    let budgetChanged = false;
                    if (params.budget_id !== undefined) {
                        if (budgetIds.has(String(params.budget_id))) {
                            budgetChanged = String(params.budget_id) !== String(values.budget_id);
                            setFieldValue('budget_id', params.budget_id);
                        } else {
                            skipped.push($t('Budget version'));
                        }
                    }

                    if (params.comparison_budget_id !== undefined) {
                        // Falsy (''/null) means "no comparison" — always applies, never flagged.
                        if (!params.comparison_budget_id || budgetIds.has(String(params.comparison_budget_id))) {
                            setFieldValue('comparison_budget_id', params.comparison_budget_id || '');
                        } else {
                            skipped.push($t('Comparison version'));
                        }
                    }

                    if (params.show_workforce_details !== undefined) {
                        setFieldValue('show_workforce_details', params.show_workforce_details);
                    }
                    if (params.include_unallocated !== undefined) {
                        setFieldValue('include_unallocated', params.include_unallocated);
                    }

                    if (params.column_preset !== undefined) {
                        setColumnPreset(params.column_preset);
                    }
                    if (params.visible_columns && typeof params.visible_columns === 'object') {
                        Object.entries(params.visible_columns).forEach(([key, visible]) => setColumnVisibility(key, !!visible));
                    }

                    if (params.hide_empty_rows !== undefined) {
                        setHideEmptyRows(params.hide_empty_rows);
                    }

                    if (Array.isArray(params.scope)) {
                        if (budgetChanged) {
                            // Applied by the [nodes] effect after the new
                            // budget's departments have been re-fetched.
                            restoreScopeRef.current = params.scope;
                        } else {
                            const leafSet = new Set(collectLeafIds(nodes));
                            setSelection(params.scope.filter((id) => leafSet.has(id)));
                        }
                    }

                    if (skipped.length > 0) {
                        toast.warning($t('Some settings could not be applied and were left unchanged: {{items}}', { items: skipped.join(', ') }));
                    }
                };

                return (
                    <div className="tw-space-y-4">
                        <SavedConfigBar
                            indexUrl={reportConfigsIndexUrl}
                            storeUrl={reportConfigsStoreUrl}
                            deleteUrlTemplate={reportConfigsDeleteUrl}
                            buildSnapshot={buildSnapshot}
                            onApply={applySnapshot}
                        />

                        {/* ---------------------------------------------- Scope */}
                        <div className={ZONE}>
                            <div className={ZONE_TITLE}>{$t('Scope')}</div>
                            <div className={ZONE_GRID}>
                                <div>
                                    <ConfigSelect
                                        name="budget_id"
                                        label={$t('Budget version')}
                                        required
                                        disabled={controlsDisabled}
                                        value={values.budget_id}
                                        error={touched.budget_id && errors.budget_id}
                                        onBlur={handleBlur}
                                        onChange={(event) => setFieldValue('budget_id', event.target.value)}
                                    >
                                        {configs.budgets?.map(({ id, name }) => (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        ))}
                                    </ConfigSelect>
                                </div>

                                <div>
                                    <ConfigSelect
                                        name="comparison_budget_id"
                                        label={$t('Compare to')}
                                        disabled={controlsDisabled}
                                        value={values.comparison_budget_id}
                                        onBlur={handleBlur}
                                        onChange={(event) => setFieldValue('comparison_budget_id', event.target.value)}
                                    >
                                        <option value="">{$t('No comparison')}</option>
                                        {configs.budgets
                                            ?.filter(({ id }) => String(id) !== String(values.budget_id))
                                            .map(({ id, name }) => (
                                                <option key={id} value={id}>
                                                    {name}
                                                </option>
                                            ))}
                                    </ConfigSelect>
                                    {/* Only worth saying once a comparison is actually chosen. */}
                                    {!!values.comparison_budget_id && (
                                        <div className="tw-mt-2.5 tw-rounded-e-lg tw-border-s-[3px] tw-border-blue tw-bg-blue-25 tw-px-3 tw-py-2.5 tw-text-xs tw-leading-[1.5]">
                                            {$t('Comparison is available on all report levels and is shown at lifetime level only.')}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <ConfigSelect
                                        name="format"
                                        label={$t('Format')}
                                        required
                                        disabled={controlsDisabled}
                                        value={values.format}
                                        error={touched.format && errors.format}
                                        onBlur={handleBlur}
                                        onChange={(event) => setFieldValue('format', event.target.value)}
                                    >
                                        <option value={FORMAT_GL_CODE}>{$t('GL Code')}</option>
                                        <option value={FORMAT_BUDGET_CODE}>{$t('Budget Code')}</option>
                                    </ConfigSelect>
                                </div>

                                <div className="tw-col-span-3">
                                    <FieldLabel>{$t('Report level')}</FieldLabel>
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-[22px] tw-gap-y-2">
                                        <SegmentedControl
                                            variant="inset"
                                            options={LEVEL_OPTIONS}
                                            value={level}
                                            onChange={(newLevel) => {
                                                if (controlsDisabled) {
                                                    return;
                                                }

                                                // Workforce details only apply to Full details.
                                                if (newLevel !== LEVEL_FULL_DETAILS && values.show_workforce_details) {
                                                    setFieldValue('show_workforce_details', false);
                                                }

                                                updateLevel(newLevel);
                                            }}
                                        />
                                        <ToggleFormField
                                            label={$t('Workforce Details')}
                                            type="checkbox"
                                            name="show_workforce_details"
                                            id="show_workforce_details"
                                            disabled={level !== LEVEL_FULL_DETAILS}
                                            checked={level === LEVEL_FULL_DETAILS && values.show_workforce_details}
                                            onChange={() => {
                                                if (level !== LEVEL_FULL_DETAILS) {
                                                    return;
                                                }

                                                setFieldValue('show_workforce_details', !values.show_workforce_details);
                                            }}
                                        ></ToggleFormField>
                                    </div>
                                    <p className={FIELD_HINT}>{LEVEL_HINTS[level]}</p>
                                </div>

                                <div className="tw-col-span-3">
                                    {_.isObject(departmentsList) && 'expenditure' in departmentsList && 'revenue' in departmentsList && (
                                        <>
                                            <FieldLabel>{$t('Departments and cost centres')}</FieldLabel>
                                            <ReportScopeSelector
                                                departments={departmentsList}
                                                value={selection}
                                                onChange={setSelection}
                                                disabled={controlsDisabled}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ----------------------------------- Period and output */}
                        <div className={ZONE}>
                            <div className={ZONE_TITLE}>{$t('Period and output')}</div>
                            <div className="tw-space-y-[18px]">
                                <div>
                                    <FieldLabel>{$t('Reporting period')}</FieldLabel>
                                    <div className="tw-flex tw-flex-wrap tw-gap-[7px]">
                                        {PERIOD_PRESET_OPTIONS.map((option) => {
                                            const active = periodPreset === option.value;

                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    disabled={controlsDisabled}
                                                    aria-pressed={active}
                                                    onClick={() => applyPeriodPreset(option.value)}
                                                    className={classNames(
                                                        'tw-rounded-2xl tw-border tw-px-[13px] tw-py-1.5 tw-text-[12.5px] tw-transition-colors disabled:tw-opacity-50',
                                                        active
                                                            ? 'tw-border-charcoal tw-bg-charcoal tw-text-white tw-font-medium'
                                                            : 'tw-border-grey tw-bg-white tw-text-charcoal-75 tw-font-normal hover:tw-border-charcoal-25',
                                                    )}
                                                >
                                                    {option.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Month pickers: filled by the preset, editable only on Custom. */}
                                    <div className="tw-mt-3 tw-flex tw-items-end tw-gap-3">
                                        <div className="tw-flex-1 tw-max-w-[190px]">
                                            <FieldLabel htmlFor="start_date">{$t('From')}</FieldLabel>
                                            <DatePickerField
                                                name="start_date"
                                                dateFormat="MM-yyyy"
                                                showMonthYearPicker
                                                className={classNames(
                                                    FIELD_INPUT,
                                                    '!tw-bg-grey/50',
                                                    errors.start_date && touched.start_date && '!tw-ring-1 !tw-ring-red',
                                                )}
                                                disabled={controlsDisabled || !isCustomPeriod}
                                                value={values.start_date}
                                                min={configs.minDate}
                                                max={configs.maxDate}
                                                onChange={(name, val) => setFieldValue('start_date', format(startOfMonth(new Date(val)), 'yyyy-MM-dd'))}
                                            />
                                        </div>
                                        <div className="tw-pb-[11px] tw-text-[12.5px] tw-text-charcoal-50">{$t('to')}</div>
                                        <div className="tw-flex-1 tw-max-w-[190px]">
                                            <FieldLabel htmlFor="end_date">{$t('To')}</FieldLabel>
                                            <DatePickerField
                                                name="end_date"
                                                dateFormat="MM-yyyy"
                                                showMonthYearPicker
                                                className={classNames(
                                                    FIELD_INPUT,
                                                    '!tw-bg-grey/50',
                                                    errors.end_date && touched.end_date && '!tw-ring-1 !tw-ring-red',
                                                )}
                                                disabled={controlsDisabled || !isCustomPeriod}
                                                value={values.end_date}
                                                min={configs.minDate}
                                                max={configs.maxDate}
                                                onChange={(name, val) => setFieldValue('end_date', format(endOfMonth(new Date(val)), 'yyyy-MM-dd'))}
                                            />
                                        </div>
                                    </div>
                                    {errors.start_date && touched.start_date && <div className={FIELD_ERROR}>{errors.start_date}</div>}
                                    {errors.end_date && touched.end_date && <div className={FIELD_ERROR}>{errors.end_date}</div>}
                                </div>

                                <div>
                                    <FieldLabel>{$t('Columns')}</FieldLabel>
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-[22px] tw-gap-y-2.5">
                                        <SegmentedControl variant="inset" options={COLUMN_PRESET_OPTIONS} value={columnPreset} onChange={setColumnPreset} />
                                        <label className="inbud-toggle">
                                            <input
                                                type="checkbox"
                                                className="input tw-peer"
                                                checked={hideEmptyRows}
                                                onChange={() => setHideEmptyRows(!hideEmptyRows)}
                                            />
                                            <div className="switch tw-peer peer-checked:tw-bg-blue peer-checked:after:tw-left-[23px]"></div>
                                            <span className="label">{$t('Hide rows with no values')}</span>
                                        </label>
                                        <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                            <ToggleFormField
                                                label={$t('Include unallocated transactions')}
                                                type="checkbox"
                                                name="include_unallocated"
                                                id="include_unallocated"
                                                checked={values.include_unallocated}
                                                onChange={() => setFieldValue('include_unallocated', !values.include_unallocated)}
                                            ></ToggleFormField>
                                            <Tooltip
                                                placement="top"
                                                tooltipType="text"
                                                tooltipContent={$t(
                                                    'Includes imported actuals not yet allocated to a department or cost centre. These appear only at the Full details level.',
                                                )}
                                                backgroundClass="tw-bg-charcoal tw-z-[9999]"
                                            >
                                                <div className="tw-flex tw-items-center">
                                                    <CircleInfoIcon className="tw-w-4 tw-h-4 tw-text-blue" />
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <button
                                            type="button"
                                            className="tw-flex tw-items-center tw-gap-x-1 tw-text-xs tw-font-medium tw-text-blue"
                                            aria-expanded={showCustomiseColumns}
                                            onClick={() => setShowCustomiseColumns((prev) => !prev)}
                                        >
                                            {$t('Customise columns')}
                                            <ChevronDownIcon
                                                className={classNames('tw-w-3 tw-h-3 tw-transition-transform', showCustomiseColumns && 'tw-rotate-180')}
                                            />
                                        </button>
                                    </div>
                                    <p className={FIELD_HINT}>
                                        {$t('Always included')}: <b className="tw-font-semibold">{$t('Month')}</b> ({$t('the ‘To’ month')}) ·{' '}
                                        <b className="tw-font-semibold">{$t('Reporting period')}</b> ({$t('From–To')}) ·{' '}
                                        <b className="tw-font-semibold">{$t('Lifetime')}</b> ({$t('total project')}).{' '}
                                        {$t('Presets control the supplementary columns.')}
                                    </p>

                                    {showCustomiseColumns && (
                                        <div className="tw-mt-3 tw-grid tw-grid-cols-4 tw-gap-x-[18px] tw-gap-y-2 tw-rounded-[10px] tw-bg-background tw-px-4 tw-py-3.5">
                                            {CUSTOMISE_COLUMN_OPTIONS.map((option) => (
                                                <CheckboxInput
                                                    key={option.key}
                                                    id={`column-${option.key}`}
                                                    label={option.label}
                                                    checked={!!visibleColumns[option.key]}
                                                    onChange={() => setColumnVisibility(option.key, !visibleColumns[option.key])}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ------------------------------------------ Named output */}
                        <div className="tw-flex tw-items-start tw-justify-end tw-gap-4 tw-pt-1.5">
                            <div className="tw-flex-1 tw-max-w-[360px]">
                                <input
                                    type="text"
                                    name="name"
                                    aria-label={$t('Report name')}
                                    placeholder={$t('Report name')}
                                    className={classNames(FIELD_INPUT, errors.name && touched.name && '!tw-ring-1 !tw-ring-red')}
                                    disabled={controlsDisabled}
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={(event) => setFieldValue('name', event.target.value)}
                                />
                                {errors.name && touched.name && <div className={FIELD_ERROR}>{errors.name}</div>}
                            </div>
                            <Button
                                className="!tw-h-auto !tw-px-6 !tw-py-[11px] !tw-text-[13.5px] !tw-font-medium disabled:tw-bg-blue-25"
                                variant="primary"
                                onClick={() => handleSubmit()}
                                disabled={loading}
                                loading={loading}
                            >
                                {$t('Generate Report')}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
}
